import { useEffect, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { Check, ClipboardList, Hammer, Hand, Loader2, PhoneCall, Send, X } from 'lucide-react'
import { Button, FormError, Link, TIER_LABELS } from '../../../core/index.js'
import { useBoardActions, useCensusDecisions, useHoldScheduleRun } from '../../hooks.js'
import type { Decision, DecisionSeverity, DecisionsView } from '../../types.js'
import { OpenCalloutModal } from '../OpenCalloutModal.js'
import { ReasonForm } from './ReasonForm.js'
import { matchesTodayFilter, type TodayFilter } from './todayData.js'

const SEVERITY_ORDER: readonly DecisionSeverity[] = ['urgent', 'approval', 'proposed', 'reminder']

const GROUP_LABEL: Record<DecisionSeverity, string> = { urgent: 'Urgent', approval: 'Approvals', proposed: 'Proposed', reminder: 'Reminders' }
const GROUP_HINT: Record<DecisionSeverity, string> = {
  urgent: 'starting soon, a seat nobody holds',
  approval: 'held by the rules for a person',
  proposed: 'census changes and seats the engine would not post alone',
  reminder: 'small things to keep the engine right',
}
const DOT: Record<DecisionSeverity, string> = { urgent: 'bg-coral', approval: 'bg-gold', proposed: 'bg-marine', reminder: 'bg-silver' }
const FRAME: Record<DecisionSeverity, string> = { urgent: 'border-coral', approval: 'border-gold/60 bg-gold-lt/40', proposed: 'border-marine/25', reminder: 'border-marine/15' }

/** How many of a group show before Show all. */
/** A group shows one row of panels (four at the widest, the user's layout of 28 October 2026) before Show all. */
const SHOWN = 4

/** Swaps held only because the two are in different tiers: the routine kind a bulk approval may settle (plan §3b). */
function isTierOnlySwap(decision: Decision): boolean {
  return decision.kind === 'swap_approval' && decision.reasoning.length > 0 && decision.reasoning.filter((line) => line.startsWith('The ') && !line.startsWith('The two are in different tiers')).length === 0 && decision.reasoning.includes('The two are in different tiers')
}

const SHIFT_TIMES: Record<string, { start: string; end: string }> = { day: { start: '06:00', end: '14:00' }, evening: { start: '14:00', end: '22:00' }, night: { start: '22:00', end: '06:00' } }

interface DecisionRowProps {
  decision: Decision
  facilityId: string
  canOpenCallout: boolean
  onRecordCensus: () => void
  monthHref: string
}

/** One thing to decide: what it is, the engine's reasoning, and the buttons that settle it. */
function DecisionRow({ decision, facilityId, canOpenCallout, onRecordCensus, monthHref }: DecisionRowProps) {
  const period = decision.source.period
  const board = useBoardActions(facilityId, period)
  const census = useCensusDecisions(facilityId)
  const hold = useHoldScheduleRun(facilityId)
  const [asking, setAsking] = useState<'run_short' | { assign: string } | null>(null)
  const [calloutOpen, setCalloutOpen] = useState(false)
  const has = (kind: Decision['actions'][number]['kind']) => decision.actions.find((action) => action.kind === kind) ?? null
  const failure = board.assign.error ?? board.runShort.error ?? board.decideSwap.error ?? board.postGap.error ?? census.apply.error ?? census.dismiss.error ?? hold.error
  const pending = board.assign.isPending || board.runShort.isPending || board.decideSwap.isPending || board.postGap.isPending || census.apply.isPending || census.dismiss.isPending
  const posting = board.postGap.isPending
  // Which of this decision's buttons is in flight, so that one shows it (the loading standard, case 6).
  const deciding = (choice: 'approve' | 'deny') => board.decideSwap.isPending && board.decideSwap.variables?.swapId === decision.source.id && board.decideSwap.variables.decision === choice
  const applying = census.apply.isPending && census.apply.variables === decision.source.id
  // A short shift may be several seats; the candidates' Assign fills the first, and the next read names the next.
  const seats = decision.seats ?? 1
  // An assignment is two waits: the board's write, then the decisions read again so the row goes. Both are said (the loading standard, case 6).
  const assigningId = board.assign.isPending ? board.assign.variables?.workerId ?? null : null
  const assignedId = board.assign.isSuccess ? board.assign.variables?.workerId ?? null : null
  const refreshing = useIsFetching({ queryKey: ['schedule-decisions'] }) > 0
  const nameOf = (workerId: string | null) => decision.candidates?.find((candidate) => candidate.workerId === workerId)?.name ?? 'The worker'
  const resetAssign = board.assign.reset
  useEffect(() => {
    // Once the list is read again the row shows the next seat, or is gone; the note has said its piece.
    if (assignedId !== null && !refreshing) {
      resetAssign()
    }
  }, [assignedId, refreshing, resetAssign])

  return (
    <div className={`flex h-full min-w-0 flex-col gap-2 rounded border px-3 py-2.5 ${FRAME[decision.severity]}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[decision.severity]}`} aria-hidden="true" />
        <div className="min-w-0 text-xs font-black leading-snug text-ink">{decision.title}</div>
      </div>
      <ul className="ml-4 list-disc space-y-0.5 text-[11px] leading-relaxed text-ink-mid">
        {decision.reasoning.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {decision.kind === 'locked_gap' && (decision.candidates?.length ?? 0) > 0 && (
        <div className="ml-4 flex flex-wrap gap-1.5">
          {decision.candidates?.map((candidate) => (
            <button
              key={candidate.workerId}
              type="button"
              disabled={pending || assignedId !== null}
              aria-busy={assigningId === candidate.workerId}
              className="inline-flex items-center gap-1 rounded-full border border-marine/25 bg-white px-2.5 py-1 text-[11px] font-bold text-marine hover:border-marine disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-marine/25"
              title={candidate.wouldBreak.length > 0 ? `Would pass: ${candidate.wouldBreak.join(', ')} - a reason code is asked` : 'Assign'}
              onClick={() => (candidate.wouldBreak.length > 0 ? setAsking({ assign: candidate.workerId }) : board.assign.mutate({ postingId: decision.source.id, workerId: candidate.workerId, reasonCode: 'ASSIGNED_FROM_TODAY' }))}
            >
              {assigningId === candidate.workerId ? (
                <>
                  <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden="true" /> Assigning {candidate.name}…
                </>
              ) : (
                <>
                  Assign {candidate.name} · {TIER_LABELS[candidate.tier]} · {candidate.weekHours} h
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {(assigningId !== null || assignedId !== null) && asking === null && (
        <p role="status" className="ml-4 inline-flex items-center gap-1.5 text-xs font-bold text-marine">
          <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden="true" />
          {assignedId !== null ? `${nameOf(assignedId)} is assigned and told. Updating what needs you…` : `Assigning ${nameOf(assigningId)}: placing them on the schedule and telling them…`}
        </p>
      )}

      {asking === 'run_short' && (
        <ReasonForm
          label={seats > 1 ? `Run all ${seats} short` : 'Run short'}
          pending={pending}
          onCancel={() => setAsking(null)}
          onSubmit={(reasonCode) => {
            // Every open seat of the short shift, one after another, under the one reason.
            void (async () => {
              for (const postingId of decision.postingIds ?? [decision.source.id]) {
                await board.runShort.mutateAsync({ postingId, reasonCode })
              }
              setAsking(null)
            })().catch(() => undefined)
          }}
        />
      )}
      {asking !== null && asking !== 'run_short' && (
        <ReasonForm label="Assign" pending={pending} onCancel={() => setAsking(null)} onSubmit={(reasonCode) => board.assign.mutate({ postingId: decision.source.id, workerId: asking.assign, reasonCode }, { onSuccess: () => setAsking(null) })} />
      )}

      {asking === null && (
        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
          {has('post_gap') && decision.date && decision.unitId && decision.shiftType && decision.role && (
            <Button
              icon={Send}
              size="sm"
              busy={posting}
              disabled={pending}
              onClick={() => board.postGap.mutate({ date: decision.date as string, unitId: decision.unitId as string, shiftType: decision.shiftType as NonNullable<Decision['shiftType']>, role: decision.role as NonNullable<Decision['role']>, seats })}
            >
              {posting ? 'Posting…' : seats > 1 ? `Post all ${seats} to the board` : 'Post it to the board'}
            </Button>
          )}
          {has('open_callout') && canOpenCallout && (
            <Button icon={PhoneCall} size="sm" onClick={() => setCalloutOpen(true)}>
              Open a Bridge callout
            </Button>
          )}
          {has('run_short') && (
            <Button icon={X} size="sm" variant="secondary" onClick={() => setAsking('run_short')}>
              {decision.kind === 'ended_unfilled' ? (seats > 1 ? `Record all ${seats} ran short` : 'Record it ran short') : seats > 1 ? `Run all ${seats} short` : 'Run short'}
            </Button>
          )}
          {decision.kind === 'ended_unfilled' && <span className="self-center text-[11px] text-ink-mid">or leave {seats > 1 ? 'them' : 'it'}: this reminder goes on its own.</span>}
          {has('approve') && (
            <Button icon={Check} size="sm" busy={deciding('approve')} disabled={pending} onClick={() => board.decideSwap.mutate({ swapId: decision.source.id, decision: 'approve' })}>
              {deciding('approve') ? 'Approving…' : 'Approve'}
            </Button>
          )}
          {has('deny') && (
            <Button icon={X} size="sm" variant="secondary" busy={deciding('deny')} disabled={pending} onClick={() => board.decideSwap.mutate({ swapId: decision.source.id, decision: 'deny' })}>
              {deciding('deny') ? 'Denying…' : 'Deny'}
            </Button>
          )}
          {has('apply') && (
            <Button icon={Check} size="sm" busy={applying} disabled={pending} onClick={() => census.apply.mutate(decision.source.id)}>
              {applying ? 'Applying…' : decision.kind === 'proposed_rise' ? 'Post the seats' : 'Apply'}
            </Button>
          )}
          {has('dismiss') && (
            <Button icon={X} size="sm" variant="secondary" busy={census.dismiss.isPending && census.dismiss.variables === decision.source.id} disabled={pending} onClick={() => census.dismiss.mutate(decision.source.id)}>
              {decision.kind === 'proposed_rise' ? 'Leave it' : 'Keep the shifts'}
            </Button>
          )}
          {(has('assign_seats') || has('release_over')) && (
            <Link to={monthHref} className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk">
              <Hammer className="h-3.5 w-3.5" aria-hidden="true" /> Open the month
            </Link>
          )}
          {has('hold') && (
            <Button icon={Hand} size="sm" variant="secondary" busy={hold.isPending} onClick={() => hold.mutate({ runId: decision.source.id, hold: true })}>
              {hold.isPending ? 'Holding…' : 'Hold the release'}
            </Button>
          )}
          {has('record_census') && (
            <Button icon={ClipboardList} size="sm" variant="secondary" onClick={onRecordCensus}>
              Record the census
            </Button>
          )}
        </div>
      )}
      <FormError error={failure} fallback="That did not go through. Try again." />
      {calloutOpen && (
        <OpenCalloutModal
          facilityId={facilityId}
          // A locked gap names its open seats; the callout is for the first, which the board then marks and fills.
          postingId={decision.kind === 'locked_gap' || decision.kind === 'ended_unfilled' ? decision.postingIds?.[0] : undefined}
          onClose={() => setCalloutOpen(false)}
          onOpened={() => setCalloutOpen(false)}
          initial={{ date: decision.date ?? '', unitId: decision.unitId ?? '', ...(decision.role ? { role: decision.role } : {}), ...(decision.coverage ? { start: decision.coverage.startTime, end: decision.coverage.endTime } : decision.shiftType ? SHIFT_TIMES[decision.shiftType] : {}) }}
        />
      )}
    </div>
  )
}

interface NeedsYouProps {
  facilityId: string
  /** False while this month has no released schedule: nothing is being filled or flexed yet, so the empty line says where the month stands instead. */
  released?: boolean
  view: DecisionsView
  filter: TodayFilter
  /** Only a facility administrator opens a Clinical Bridge callout (the user's rule, 13 October 2026). */
  canOpenCallout: boolean
  onRecordCensus: () => void
  monthHref: string
}

/** The routine kind of a group, settled in one press: the swaps held only for a tier difference, every proposed census change. */
function BulkAction({ severity, decisions, facilityId }: { severity: DecisionSeverity; decisions: readonly Decision[]; facilityId: string }) {
  const census = useCensusDecisions(facilityId)
  const period = decisions[0]?.source.period ?? null
  const board = useBoardActions(facilityId, period)
  const [running, setRunning] = useState(false)
  const tierOnly = decisions.filter(isTierOnlySwap)
  const proposals = decisions.filter((decision) => decision.kind === 'proposed_flex' || decision.kind === 'proposed_rise')
  const run = async (items: readonly Decision[], act: (decision: Decision) => Promise<unknown>) => {
    setRunning(true)
    try {
      // A few at a time, as Remind all does: each is its own request and its own conditional write.
      for (let index = 0; index < items.length; index += 5) {
        await Promise.allSettled(items.slice(index, index + 5).map(act))
      }
    } finally {
      setRunning(false)
    }
  }
  if (severity === 'approval' && tierOnly.length > 1) {
    return (
      <Button icon={Check} size="sm" busy={running} onClick={() => void run(tierOnly, (decision) => board.decideSwap.mutateAsync({ swapId: decision.source.id, decision: 'approve' }))}>
        Approve the {tierOnly.length} held only for a tier difference
      </Button>
    )
  }
  if (severity === 'proposed' && proposals.length > 1) {
    return (
      <Button icon={Check} size="sm" busy={running} onClick={() => void run(proposals, (decision) => census.apply.mutateAsync(decision.source.id))}>
        Apply all {proposals.length}
      </Button>
    )
  }
  return null
}

/**
 * Needs you (facility operations dashboard plan §3): everything the engine stopped for, grouped urgent, approvals,
 * proposed, reminders, each with the engine's reasoning and the button that settles it. A group shows its first few with
 * Show all; the routine kind of a group goes in one press. The unit and role filter narrows it.
 */
export function NeedsYou({ facilityId, view, filter, canOpenCallout, onRecordCensus, monthHref, released = true }: NeedsYouProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<DecisionSeverity>>(new Set())
  const decisions = view.decisions.filter((decision) => matchesTodayFilter(filter, decision.unitId, decision.role))
  if (decisions.length === 0) {
    return (
      <p className="text-xs text-ink-mid">
        Nothing needs you{filter.unitId || filter.role ? ' here' : ''}.{' '}
        {released ? (
          'The engine is filling, offering and flexing on its own.'
        ) : (
          <>
            This month has no released schedule, so there is nothing to fill or flex yet.{' '}
            <Link to={monthHref} className="font-bold text-marine hover:text-marine-dk">
              Open the month
            </Link>
          </>
        )}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {SEVERITY_ORDER.map((severity) => {
        const group = decisions.filter((decision) => decision.severity === severity)
        if (group.length === 0) {
          return null
        }
        const open = expanded.has(severity)
        const shown = open ? group : group.slice(0, SHOWN)
        return (
          <section key={severity} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2 border-t border-marine/10 pt-2">
              <span className={`h-2 w-2 rounded-full ${DOT[severity]}`} aria-hidden="true" />
              <h3 className="text-xs font-black text-ink">
                {GROUP_LABEL[severity]} · {group.length}
              </h3>
              <span className="text-[11px] text-ink-mid">{GROUP_HINT[severity]}</span>
              <span className="flex-1" />
              <BulkAction severity={severity} decisions={group} facilityId={facilityId} />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {shown.map((decision) => (
                <DecisionRow key={decision.id} decision={decision} facilityId={facilityId} canOpenCallout={canOpenCallout} onRecordCensus={onRecordCensus} monthHref={monthHref} />
              ))}
            </div>
            {group.length > SHOWN && (
              <button type="button" className="self-start text-xs font-bold text-marine hover:text-marine-dk" onClick={() => setExpanded((current) => new Set(open ? [...current].filter((entry) => entry !== severity) : [...current, severity]))}>
                {open ? 'Show fewer' : `Show all ${group.length}`}
              </button>
            )}
          </section>
        )
      })}
    </div>
  )
}
